import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Play, Pause, FileText, Volume2, VolumeX, Link2, Check } from 'lucide-react'
import { usePlayer } from '../lib/player'
import { getPaperSongUrl } from '../lib/data'
import { LatexText } from '../lib/latex'

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function PlayBar() {
  const {
    currentPaperId,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    volume,
    showLyrics,
    paper,
    suno,
    toggle,
    seek,
    setVolume,
    toggleLyrics,
  } = usePlayer()

  const [flashLyrics, setFlashLyrics] = useState(false)
  const [prevPaperId, setPrevPaperId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const location = useLocation()
  
  // Flash lyrics button when new song starts and lyrics are closed (mobile only)
  useEffect(() => {
    if (currentPaperId && currentPaperId !== prevPaperId && !showLyrics) {
      setFlashLyrics(true)
      const timer = setTimeout(() => setFlashLyrics(false), 2000)
      setPrevPaperId(currentPaperId)
      return () => clearTimeout(timer)
    }
    setPrevPaperId(currentPaperId)
  }, [currentPaperId, prevPaperId, showLyrics])

  // Hide on song pages and about page (they have their own controls or none)
  const isSongPage = location.pathname.startsWith('/virtual/')
  const isAboutPage = location.pathname === '/about'
  if (isSongPage || isAboutPage) return null

  // Don't show if nothing is playing
  if (!currentPaperId || !suno) return null

  const coverUrl = suno.image_url || suno.image_large_url
  const title = suno.title || paper?.title || 'Unknown'
  const authors = paper?.authors || ''
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const songUrl = getPaperSongUrl(currentPaperId)

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    seek(Math.max(0, Math.min(duration, percent * duration)))
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value))
  }

  const handleCopyLink = async () => {
    try {
      const fullUrl = `${window.location.origin}${songUrl}`
      await navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:right-[340px]">
      <div className="bg-surface-900/95 backdrop-blur-lg border-t border-surface-700/50">
        {/* Progress bar - full width, clickable */}
        <div 
          className="h-1 bg-surface-800 cursor-pointer group"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-accent-500 relative transition-all duration-100"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-accent-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
          </div>
        </div>

        <div className="px-4 py-3">
          {/* 3-column layout */}
          <div className="grid grid-cols-3 items-center gap-4">
            {/* Left: Cover + Info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0">
                {coverUrl ? (
                  <img 
                    src={coverUrl} 
                    alt={title}
                    className="w-12 h-12 rounded-lg object-cover shadow-lg"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-surface-700 flex items-center justify-center">
                    <svg className="w-6 h-6 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 hidden sm:block">
                <Link 
                  to={songUrl}
                  className="text-surface-100 font-medium text-sm truncate block hover:text-accent-300 transition-colors"
                >
                  <LatexText>{title}</LatexText>
                </Link>
                <p className="text-surface-400 text-xs truncate">{authors}</p>
              </div>
            </div>

            {/* Center: Controls */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-3">
                {/* Time - hidden on mobile */}
                <span className="text-surface-400 text-xs tabular-nums hidden md:block w-10 text-right">
                  {formatTime(currentTime)}
                </span>

                {/* Play/Pause */}
                <button
                  onClick={toggle}
                  disabled={isLoading}
                  className="w-10 h-10 rounded-full bg-surface-100 hover:bg-white flex items-center justify-center text-surface-900 transition-colors disabled:opacity-50 shadow-lg"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-surface-400 border-t-surface-900 rounded-full animate-spin" />
                  ) : isPlaying ? (
                    <Pause size={18} fill="currentColor" />
                  ) : (
                    <Play size={18} fill="currentColor" className="ml-0.5" />
                  )}
                </button>

                {/* Time - hidden on mobile */}
                <span className="text-surface-400 text-xs tabular-nums hidden md:block w-10">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            {/* Right: Share + Volume + Lyrics (mobile only) */}
            <div className="flex items-center justify-end gap-2">
              {/* Share button - hidden on mobile */}
              <button
                onClick={handleCopyLink}
                className="hidden md:flex w-8 h-8 rounded-full items-center justify-center text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors"
                aria-label="Copy link"
              >
                {copied ? <Check size={16} className="text-green-400" /> : <Link2 size={16} />}
              </button>

              {/* Volume control - hidden on mobile */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => setVolume(volume > 0 ? 0 : 1)}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                  aria-label={volume > 0 ? 'Mute' : 'Unmute'}
                >
                  {volume > 0 ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-24 h-1 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-3
                    [&::-webkit-slider-thumb]:h-3
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-surface-100
                    [&::-webkit-slider-thumb]:hover:bg-white
                    [&::-webkit-slider-thumb]:transition-colors
                    [&::-webkit-slider-thumb]:mt-[-3px]
                    [&::-moz-range-thumb]:w-3
                    [&::-moz-range-thumb]:h-3
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-surface-100
                    [&::-moz-range-thumb]:border-0"
                  style={{
                    background: `linear-gradient(to right, #f59e0b ${volume * 100}%, #334155 ${volume * 100}%)`
                  }}
                  aria-label="Volume"
                />
              </div>

              {/* Lyrics toggle - mobile only */}
              <button
                onClick={toggleLyrics}
                className={`
                  lg:hidden
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all
                  ${showLyrics 
                    ? 'bg-accent-500/20 text-accent-400' 
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50'
                  }
                  ${flashLyrics ? 'animate-pulse ring-2 ring-accent-400' : ''}
                `}
                aria-label={showLyrics ? 'Hide lyrics' : 'Show lyrics'}
              >
                <FileText size={16} />
                <span className="text-sm">Lyrics</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
